import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useGate } from 'statsig-react';
import { twMerge } from 'tailwind-merge';
import type { Address } from 'viem';
import {
  faCcAmex,
  faCcApplePay,
  faCcVisa,
} from '@soundxyz/font-awesome/free-brands-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import type { MintSchedule } from '@soundxyz/sdk';
import { minterAbiMap } from '@soundxyz/sdk/utils/constants';
import type { PayWithCCSource } from '@soundxyz/utils/analytics';
import { NULL_ADDRESS } from '@soundxyz/utils/constants/constants';
import { ERROR_TYPE, MINTING_ERROR_ACTIONS, PILLARS } from '@soundxyz/utils/constants/logging';
import { GooglePay } from '@components/icons/GooglePay';
import { MasterCard } from '@components/icons/MasterCard';
import type { ButtonProps } from '@components/main-components/button/Button';
import { Button } from '@components/main-components/button/Button';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { useMintReferral } from '@context/mintReferral';
import { ModalContext, ModalType } from '@context/modal';
import { ToastContext } from '@context/toast';
import type {
  BuyEditionSongDetailsFragmentDoc,
  ContractType,
  EditionCollectableSchedulesInfoFragment,
  FragmentType,
} from '@graphql/reactQuery';
import { PayEditionWithCreditCardDocument, useQuery } from '@graphql/reactQuery';
import { useCheckReferrableRelease } from '@hooks/useCheckReferrable';
import { useLogError } from '@hooks/useLogErrorNew';
import { trackClickEvent } from '@lib/analytics';
import { bigIntToNumber } from '@utils/bigNumberHelpers';
import { FEATURE_GATES } from '@utils/constants';
import { getMerkleProof } from '@utils/merkle';
import {
  CrossMintEditionCreditCardModal,
  CrossMintSamCreditCardModal,
} from './CrossMint/CCMintModal';
import { PaperCCMintModal } from './Paper/CCMintModal';
import { usePaperEditionCheckout, usePaperSamCheckout } from './Paper/Checkout';
import { ALLOWED_INTERFACES_FOR_CC, useIsCreditCardOnEditionAllowed } from './credit-card-helpers';

/*#__PURE__*/ gql(/* GraphQL */ `
  query PayEditionWithCreditCard($releaseId: UUID!) {
    release(id: $releaseId) {
      id
      ...ReleaseReferrableInfo
      ...BuyEditionSongDetails
    }
  }
`);

function PayWithCreditCard({
  onClick,
  disabled,
  loading,
  className,
  variant,
}: {
  className?: string;
  onClick: () => void;
  disabled: boolean;
  loading: boolean;
  variant: ButtonProps['variant'];
}) {
  return (
    <Button
      trailingIcon={
        <div className="flex items-center justify-center gap-2">
          {/* @ts-expect-error this is a WebKit Feature https://webkit.org/blog/8182/introducing-the-payment-request-api-for-apple-pay/  */}
          {window?.ApplePaySession && ApplePaySession?.canMakePayments() ? (
            <FontAwesomeIcon icon={faCcApplePay} fontSize={24} />
          ) : (
            <div className="-mr-2">
              <GooglePay width={60} height={60} />
            </div>
          )}
          <FontAwesomeIcon icon={faCcVisa} fontSize={24} className="text-[#0E4595]" />
          <div className="rounded-sm bg-[#231F20]">
            <MasterCard width={24} height={22} />
          </div>
          <FontAwesomeIcon icon={faCcAmex} fontSize={24} className="text-[#2557D6]" />
        </div>
      }
      disabled={disabled}
      loading={loading}
      label="Pay with Card"
      variant={variant}
      size="XL"
      fullWidth
      className={twMerge(loading ? '' : 'justify-between', className)}
      onClick={onClick}
    />
  );
}

export function PayEditionWithCreditCard({
  releaseId,
  releaseContractAddress,
  scheduleIds,
  schedule,
  chainId,
  quantity,
  canCollectSafely,
  disabled,
  className,
  variant,
  source,
  auctionContractType,
  giftTo,
}: {
  releaseId: string;
  releaseContractAddress: string;
  scheduleIds: EditionCollectableSchedulesInfoFragment['scheduleIds'] | null | undefined;
  schedule: MintSchedule;
  chainId: number;
  quantity: number;
  canCollectSafely: boolean;
  disabled: boolean;
  className?: string;
  variant: ButtonProps['variant'];
  giftTo: Address | null;
  /** This is important for analytics. Helps us figure out what place user clicked from  */
  source: PayWithCCSource;
  auctionContractType: ContractType | null;
}) {
  const { authUserAddress } = useAuth();
  const { provider } = useConnectedAuth();
  const logError = useLogError();
  const { value: crossMintCcCheckout } = useGate(FEATURE_GATES.CROSSMINT_CC_CHECKOUT);
  // TODO: Consolidate https://linear.app/sound/issue/ENG-6308/consolidate-paper-checkout-to-one-feature-flag
  const { value: paperCCMintCheckout } = useGate(FEATURE_GATES.MINTERS_CREDIT_CARD_CHECKOUT);

  // TODO: https://linear.app/sound/issue/ENG-8133/implement-credit-card-purchase-with-tiers-on-webapp
  const {
    isAllowed: isCreditCardAllowed,
    activeMintSchedule,
    totalPriceEth,
  } = useIsCreditCardOnEditionAllowed({
    releaseContractAddress,
    scheduleIds,
    schedule,
    chainId,
    quantity,
    auctionContractType,
  });
  const { getPaperKey, isLoading: creditCardButtonIsLoading } = usePaperEditionCheckout({
    releaseId,
    releaseContractAddress,
    scheduleIds,
    schedule,
    chainId,
    quantity,
    type: 'CREDIT_CARD',
    auctionContractType,
    giftTo,
  });
  const { openModal } = ModalContext.useContainer();
  const { openToast } = ToastContext.useContainer();

  const { data } = useQuery(PayEditionWithCreditCardDocument, {
    variables: {
      releaseId,
    },
    staleTime: 0,
  });

  const release = data?.data.release;

  const { getAffiliate } = useCheckReferrableRelease({
    release,
  });

  const { referral } = useMintReferral();

  const creditCardLoading = !canCollectSafely || creditCardButtonIsLoading;

  // we just hide the button if we do not allow CC
  if (!isCreditCardAllowed) {
    return null;
  }

  const toAddress = giftTo ?? authUserAddress ?? '';
  if (crossMintCcCheckout) {
    return (
      <PayWithCreditCard
        disabled={disabled}
        loading={creditCardLoading}
        className={className}
        variant={variant}
        onClick={async () => {
          if (!release) return;
          if (!activeMintSchedule) return;
          if (!authUserAddress) return;

          const minterAddress = activeMintSchedule.minterAddress.toLowerCase();
          const affiliateAddress = (getAffiliate(referral) || NULL_ADDRESS).toLowerCase();

          trackClickEvent({
            event: 'Pay with Credit Card Clicked',
            properties: {
              associationId: releaseId,
              associationType: 'Release',
              sourceComponent: source,
              minterAddress,
              affiliateAddress,
              chainId,
              quantity,
              mintTo: toAddress,
            },
          });

          let proof: string[] | null = null;
          if (
            ALLOWED_INTERFACES_FOR_CC.IMerkleDropMinterV2 === activeMintSchedule.interfaceId ||
            ALLOWED_INTERFACES_FOR_CC.IMerkleDropMinterV2_1 === activeMintSchedule.interfaceId
          ) {
            const estimateProvider = provider(chainId);

            const { merkleRootHash: merkleRoot } = await (activeMintSchedule.interfaceId ===
            ALLOWED_INTERFACES_FOR_CC.IMerkleDropMinterV2
              ? estimateProvider.readContract({
                  abi: minterAbiMap[activeMintSchedule.interfaceId],
                  address: activeMintSchedule.minterAddress,
                  functionName: 'mintInfo',
                  args: [activeMintSchedule.editionAddress, activeMintSchedule.mintId],
                })
              : estimateProvider.readContract({
                  abi: minterAbiMap[activeMintSchedule.interfaceId],
                  address: activeMintSchedule.minterAddress,
                  functionName: 'mintInfo',
                  args: [activeMintSchedule.editionAddress, activeMintSchedule.mintId],
                }));

            proof = await getMerkleProof({
              merkleRoot,
              userAddress: authUserAddress,
            });
          }

          return openModal(ModalType.CROSSMINT_CC_MINT_MODAL, {
            body: (
              <CrossMintEditionCreditCardModal
                minterAddress={minterAddress}
                quantity={quantity}
                data={release}
                contractAddress={releaseContractAddress}
                onSuccessfulTransaction={() => {
                  trackClickEvent({
                    event: 'Pay with Credit Card Success',
                    properties: {
                      associationId: releaseId,
                      associationType: 'Release',
                      sourceComponent: source,
                      minterAddress,
                      affiliateAddress,
                      chainId,
                      quantity,
                      mintTo: toAddress,
                      isGifted: !!giftTo,
                    },
                  });
                }}
                proof={proof}
                totalPriceEth={totalPriceEth}
                affiliate={affiliateAddress}
                contractInterFaceId={activeMintSchedule.interfaceId}
                mintId={bigIntToNumber(activeMintSchedule.mintId, { strict: true })}
              />
            ),
          });
        }}
      />
    );
  }

  if (paperCCMintCheckout) {
    return (
      <PayWithCreditCard
        disabled={disabled}
        loading={creditCardLoading}
        className={className}
        variant={variant}
        onClick={async () => {
          if (!release) return;

          const affiliate = getAffiliate(referral) || NULL_ADDRESS;

          const paperKey = await getPaperKey({ affiliate });

          if ('key' in paperKey) {
            const minterAddress = paperKey.minterAddress.toLowerCase();
            const affiliateAddress = affiliate.toLowerCase();
            trackClickEvent({
              event: 'Pay with Credit Card Clicked',
              properties: {
                associationId: releaseId,
                associationType: 'Release',
                sourceComponent: source,
                minterAddress,
                affiliateAddress,
                chainId,
                quantity,
                mintTo: toAddress,
              },
            });
            openModal(ModalType.PAPER_CC_MINT_MODAL, {
              body: (
                <PaperCCMintModal
                  selectedTier={null}
                  paperKey={paperKey.key}
                  quantity={quantity}
                  data={release}
                  onSuccessfulTransaction={() => {
                    trackClickEvent({
                      event: 'Pay with Credit Card Success',
                      properties: {
                        associationId: releaseId,
                        associationType: 'Release',
                        sourceComponent: source,
                        minterAddress,
                        affiliateAddress,
                        chainId,
                        quantity,
                        mintTo: toAddress,
                        isGifted: !!giftTo,
                      },
                    });
                  }}
                />
              ),
            });
          } else if ('error' in paperKey) {
            openToast({
              text: paperKey.error,
              variant: 'error',
            });
          } else {
            logError({
              action: MINTING_ERROR_ACTIONS.PAPER_ERROR,
              error: new Error('Error fetching paper key for PayEditionWithCreditCard'),
              closeProcessingModal: true,
              indexedTags: {
                chainId,
                releaseId,
              },
              errorType: ERROR_TYPE.PAPER_CC,
              pillar: PILLARS.MINTING,
              isArtistOnlyPage: false,
              level: 'fatal',
              message: 'Error fetching paper key for PayEditionWithCreditCard',
              toast: 'Something went wrong. Please try again.',
            });
          }
        }}
      />
    );
  }

  // No credit card provider is enabled
  return null;
}

export function PaySamWithCreditCard({
  disabled,
  quantity,
  affiliate,
  buyPriceWei,
  data,
  releaseId,
  minterAddress,
  className,
  variant,
  chainId,
  source,
  contractAddress,
  giftTo,
}: {
  releaseId: string;
  minterAddress: string;
  disabled: boolean;
  quantity: number;
  affiliate: string;
  buyPriceWei: string;
  data: FragmentType<BuyEditionSongDetailsFragmentDoc>;
  className?: string;
  variant: ButtonProps['variant'];
  chainId: number;
  /** This is important for analytics. Helps us figure out what place user clicked from  */
  source: PayWithCCSource;
  contractAddress: string;
  giftTo: Address | null;
}) {
  const minter = minterAddress.toLowerCase();
  const logError = useLogError();
  const { value: crossMintCCCheckout } = useGate(FEATURE_GATES.CROSSMINT_CC_CHECKOUT);
  // TODO: Consolidate https://linear.app/sound/issue/ENG-6308/consolidate-paper-checkout-to-one-feature-flag
  const { value: paperCCCheckout } = useGate(FEATURE_GATES.SOUND_SWAP_CREDIT_CARD_CHECKOUT);
  const { openModal } = ModalContext.useContainer();
  const { getPaperKey, isLoading: createPaperSoundSwapKeyIsLoading } = usePaperSamCheckout({
    releaseId,
    buyPriceWei,
    quantity,
    type: 'CREDIT_CARD',
  });

  const { openToast } = ToastContext.useContainer();

  const { authUserAddress } = useAuth();
  const toAddress = giftTo ?? authUserAddress ?? '';
  if (crossMintCCCheckout) {
    return (
      <PayWithCreditCard
        variant={variant}
        disabled={disabled}
        loading={createPaperSoundSwapKeyIsLoading}
        className={className}
        onClick={async () => {
          const affiliateAddress = affiliate.toLowerCase();
          trackClickEvent({
            event: 'Pay with Credit Card Clicked',
            properties: {
              associationId: releaseId,
              associationType: 'Release',
              sourceComponent: source,
              minterAddress: minter,
              affiliateAddress,
              chainId,
              quantity,
              mintTo: toAddress,
            },
          });
          return openModal(ModalType.CROSSMINT_CC_MINT_MODAL, {
            body: (
              <CrossMintSamCreditCardModal
                contractAddress={contractAddress}
                buyPriceWei={buyPriceWei}
                quantity={quantity}
                data={data}
                affiliate={affiliate}
                onSuccessfulTransaction={() => {
                  trackClickEvent({
                    event: 'Pay with Credit Card Success',
                    properties: {
                      associationId: releaseId,
                      associationType: 'Release',
                      sourceComponent: source,
                      minterAddress: minter,
                      affiliateAddress,
                      chainId,
                      quantity,
                      mintTo: toAddress,
                      isGifted: !!giftTo,
                    },
                  });
                }}
              />
            ),
          });
        }}
      />
    );
  }

  if (paperCCCheckout) {
    return (
      <PayWithCreditCard
        variant={variant}
        disabled={disabled}
        loading={createPaperSoundSwapKeyIsLoading}
        className={className}
        onClick={async () => {
          const paperKey = await getPaperKey({ affiliate });

          if ('key' in paperKey) {
            const affiliateAddress = affiliate.toLowerCase();
            trackClickEvent({
              event: 'Pay with Credit Card Clicked',
              properties: {
                associationId: releaseId,
                associationType: 'Release',
                sourceComponent: source,
                minterAddress: minter,
                affiliateAddress,
                chainId,
                quantity,
                mintTo: toAddress,
              },
            });
            return openModal(ModalType.PAPER_CC_MINT_MODAL, {
              body: (
                <PaperCCMintModal
                  selectedTier={null}
                  paperKey={paperKey.key}
                  quantity={quantity}
                  data={data}
                  onSuccessfulTransaction={() => {
                    trackClickEvent({
                      event: 'Pay with Credit Card Success',
                      properties: {
                        associationId: releaseId,
                        associationType: 'Release',
                        sourceComponent: source,
                        minterAddress: minter,
                        affiliateAddress,
                        chainId,
                        quantity,
                        mintTo: toAddress,
                        isGifted: !!giftTo,
                      },
                    });
                  }}
                />
              ),
            });
          } else if ('error' in paperKey) {
            return openToast({
              text: paperKey.error,
              variant: 'error',
            });
          } else {
            logError({
              action: MINTING_ERROR_ACTIONS.PAPER_ERROR,
              error: new Error('Error fetching paper key for PaySamWithCreditCard'),
              closeProcessingModal: true,
              indexedTags: {
                chainId,
                releaseId,
              },
              errorType: ERROR_TYPE.PAPER_CC,
              pillar: PILLARS.MINTING,
              isArtistOnlyPage: false,
              level: 'fatal',
              message: 'Error fetching paper key for PaySamWithCreditCard',
              toast: 'Something went wrong. Please try again.',
            });
          }
        }}
      />
    );
  }

  // Credit card checkout is not enabled
  return null;
}
